import { useEffect, useMemo, useRef, useState } from "react";
import { HardDriveDownload, Lock, ShieldCheck, Loader2, FlaskConical, DatabaseBackup, SearchCheck } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { Field } from "@/components/v52/ImportParts";
import { useFmt } from "@/components/v52/Bits";
import { useT52 } from "@/i18n/v52";

const kb = (n) => (n ? `${Math.round(n / 1024)} KB` : "—");

function BackupRow({ b }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const rc = b.restore_check;
  const tot = b.totals;
  return (
    <li data-testid={`ops-backup-${b.id}`}>
      <div>
        <h3>{t52(b.kind === "app" ? "ops_b_kind_app" : "ops_b_kind_control")} · {b.environment}</h3>
        <p className="f52-mono">{b.id}</p>
        <p className="f52-mono" data-testid={`ops-backup-${b.id}-sha`}>SHA256 {b.sha256}</p>
        <p>{fmt(b.created_at)} · {kb(b.size_bytes)}{tot ? ` · ${t52("ops_b_meta", { c: tot.collections, d: tot.documents, i: tot.indexes })}` : ""}</p>
        {b.restore_path && <p><b>{t52("ops_b_path")}:</b> {b.restore_path}</p>}
      </div>
      <div className="acts">
        <span className={`f52-chip ${rc ? (rc.ok ? "ok" : "fail") : "unk"}`} data-testid={`ops-backup-${b.id}-rc`}>
          {!rc ? t52("ops_b_rc_none") : rc.ok ? t52("ops_b_rc_pass", { env: rc.environment, t: fmt(rc.checked_at) }) : t52("ops_b_rc_bad")}
        </span>
        {b.kind === "app" && (
          <span className={`f52-chip ${b.media_ok && b.media_evidence === "restored" ? "ok" : "unk"}`} data-testid={`ops-backup-${b.id}-media`}>
            {t52(!b.media_ok ? (b.format === 1 ? "ops_b_media_v1" : "ops_b_media_open")
              : b.media_evidence === "restored" ? "ops_b_media_ok" : "ops_b_media_artifact")}
          </span>
        )}
      </div>
    </li>
  );
}

function AppJobLine({ j }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  if (!j) return null;
  const ref = j.upstream?.ref || j.late?.upstream?.ref;
  const st = j.status === "running" ? "running" : j.outcome;
  return (
    <div className="f52-imp-body" data-testid="ops-app-backup-job">
      <p className="f52-imp-line">
        <b>{t52("ops_ab_last")}:</b> <span data-testid="ops-app-backup-status">{t52(`ops_ab_out_${st}`)}</span>
        {ref && <> · <span className="f52-mono">{ref}</span></>} · {fmt(j.finished_at || j.started_at)}
        {j.reconciled && ` · ${t52("ops_ab_reconciled")}`}{j.error && ` · ${j.error}`}
      </p>
      {j.blocking && j.status !== "running" && (
        <p className="f52-imp-warn" data-testid="ops-app-backup-blocked">{t52("ops_ab_blocked")}{j.note && ` · ${j.note}`}</p>
      )}
      {j.proof && (
        <p className={j.proof.ok && j.proof.complete ? "f52-imp-line" : "f52-imp-warn"} data-testid="ops-app-backup-proof">
          {!j.proof.ok ? `${t52("ops_ab_proof_bad")}: ${(j.proof.errors || []).join("; ")}`
            : j.proof.complete ? t52(j.proof.media_evidence === "restored" ? "ops_ab_proof_ok" : "ops_ab_proof_artifact")
              : [t52("ops_ab_proof_db_only"), ...(j.proof.media_errors || [])].join(" · ")}
        </p>
      )}
    </div>
  );
}

export function BackupsPanel({ backups, isProd, busy, appJob, onCreate, onAppBackup, onAppReconcile, onRestore }) {
  const { t52 } = useT52();
  const appRunning = appJob?.status === "running";
  const appBlocked = Boolean(appJob?.blocking);
  const actions = (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={onCreate} data-testid="ops-backup-create"><HardDriveDownload aria-hidden="true" />{t52("ops_b_create")}</button>
      <button type="button" className="f52-btn f52-btn-secondary" disabled={busy || appBlocked} onClick={onAppBackup} data-testid="ops-app-backup-create">
        {appRunning ? <Loader2 className="animate-spin" aria-hidden="true" /> : <DatabaseBackup aria-hidden="true" />}{t52("ops_ab_create")}
      </button>
      {appBlocked && !appRunning && (
        <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={onAppReconcile} data-testid="ops-app-backup-reconcile">
          <SearchCheck aria-hidden="true" />{t52("ops_ab_reconcile")}
        </button>
      )}
      {!isProd && <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={() => onRestore("local")} data-testid="ops-rc-local"><FlaskConical aria-hidden="true" />{t52("ops_b_rc_local")}</button>}
      {!isProd && <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={() => onRestore("production")} data-testid="ops-rc-prod"><FlaskConical aria-hidden="true" />{t52("ops_b_rc_prod")}</button>}
    </div>
  );
  return (
    <Panel title={t52("ops_b_t")} subtitle={t52("ops_b_lead")} actions={actions} testid="ops-backups-panel">
      <AppJobLine j={appJob} />
      {!backups.length ? <p className="f52-imp-muted" data-testid="ops-backups-none">{t52("ops_b_none")}</p>
        : <ul className="f52-imp-steps">{backups.map((b) => <BackupRow key={b.id} b={b} />)}</ul>}
    </Panel>
  );
}

export function HeldPanel({ held }) {
  const { t52 } = useT52();
  return (
    <Panel title={t52("ops_h_t")} subtitle={t52("ops_h_lead")} testid="ops-held-panel">
      {!held ? <p className="f52-imp-muted" data-testid="ops-held-none">{t52("ops_h_none")}</p> : (
        <div className="f52-imp-body">
          <p className="f52-imp-line" data-testid="ops-held-orgs">{t52("ops_h_orgs")}: <b>{held.organizations.length}</b> · <span className="f52-mono">{held.organizations.join(", ")}</span></p>
          <p className="f52-imp-line">{t52("ops_h_heldop")}: <b>{held.locations_held_operator}</b> · {t52("ops_h_unclear")}: <b>{held.locations_unclear}</b> · {t52("ops_h_noop")}: <b>{held.locations_no_operator}</b></p>
        </div>
      )}
    </Panel>
  );
}

function CurrentResults({ cur }) {
  const { t52 } = useT52();
  if (!cur) return null;
  return (
    <div className="f52-imp-body" data-testid="ops-results-current">
      <p className="f52-imp-muted">{t52("ops_r_current", { id: cur.run_id, s: cur.state, n: cur.results.length })}</p>
      <ul className="f52-ops-list">
        {cur.results.map((r) => <li key={r.tenant_id}><span>{r.company_name}</span><small className="f52-mono">{r.tenant_id}</small><small>{t52("ops_r_locs", { n: r.locations })}</small></li>)}
      </ul>
    </div>
  );
}

export function ResultsPanel({ results, base, current, onBaseReport }) {
  const { t52 } = useT52();
  const [q, setQ] = useState("");
  const rows = useMemo(() => (results || []).filter((r) => (r.company_name || "").toLowerCase().includes(q.toLowerCase())), [results, q]);
  const sub = results ? `${t52("ops_r_count", { n: results.length })} · ${t52("ops_r_base", { id: base.run_id, n: base.supplements.length })}` : null;
  const actions = base && (
    <button type="button" onClick={onBaseReport} data-testid="ops-results-base-report" className="f52-btn f52-btn-secondary">
      <HardDriveDownload aria-hidden="true" />{t52("ops_r_base_report")}
    </button>
  );
  return (
    <Panel title={t52("ops_r_t")} subtitle={sub} actions={actions} testid="ops-results-panel">
      <CurrentResults cur={current} />
      {!results ? <p className="f52-imp-muted" data-testid="ops-results-none">{t52("ops_r_none")}</p> : (
        <div className="f52-imp-body">
          <label className="f52-imp-field">{t52("ops_r_filter")}<input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ops-results-filter" /></label>
          <ul className="f52-ops-list" data-testid="ops-results-list">
            {rows.slice(0, 300).map((r) => (
              <li key={r.tenant_id}><span>{r.company_name}</span><small className="f52-mono">{r.source_system} · {r.source_record_id}</small><small>{t52("ops_r_locs", { n: r.locations })}{r.sources?.some((s) => s !== base?.run_id) ? ` · ${t52("ops_r_supp_tag")}` : ""}</small></li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

export function JournalPanel({ journal }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  return (
    <Panel title={t52("ops_j_t")} testid="ops-journal-panel">
      {!journal.length ? <p className="f52-imp-muted" data-testid="ops-journal-none">{t52("ops_j_none")}</p> : (
        <ul className="f52-ops-list" data-testid="ops-journal-list">
          {journal.map((j, i) => (
            <li key={`${j.at}-${i}`}><span>{fmt(j.at)} · <b>{j.event}</b>{j.step ? ` · ${j.step}` : ""}</span><small className="f52-mono">{j.state || ""} · fence {j.fence ?? "—"}</small><small className="f52-mono">{JSON.stringify(j.detail).slice(0, 220)}</small></li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function OpsDialog({ mode, ov, onCancel, onSubmit }) {
  const { t52 } = useT52();
  const ref = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const [v, setV] = useState({ confirm: "", target_confirm: "", totp_code: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.querySelector("input")?.focus();
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); cancelRef.current(); } };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => { e.preventDefault(); setBusy(true); await onSubmit(v); setBusy(false); };
  const sup = mode === "supplement";
  const start = mode === "start" || sup;
  const supRun = sup || (mode === "resume" && ov.run?.kind === "supplement");
  const sha = supRun ? ov.supplement?.sha256 : ov.package.manifest_sha256;
  return (
    <div ref={ref} className="f52-dlg" role="alertdialog" aria-modal="true" aria-labelledby="ops-dlg-t" data-testid="ops-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <form className="box" onSubmit={submit}>
        <h2 id="ops-dlg-t">{t52(sup ? "ops_sp_d_title" : start ? "ops_d_title" : "ops_d_resume_title")}</h2>
        <p>{sup ? t52("ops_sp_d_lead", { h: ov.approval_ttl_h, id: ov.supplement?.id }) : start ? t52("ops_d_lead", { h: ov.approval_ttl_h }) : t52("ops_d_resume_lead")}</p>
        <p className="f52-imp-muted f52-mono" data-testid="ops-dialog-sha">{t52("ops_d_pkg", { p: `${(sha || "").slice(0, 16)}…` })} · {ov.target.control_db}</p>
        {ov.is_preview && <p className="f52-imp-warn" data-testid="ops-dialog-preview-note"><Lock aria-hidden="true" />{t52("ops_d_preview")}</p>}
        {start && <Field label={t52("imp_d_word", { w: sup ? ov.supplement_word : ov.start_word })} value={v.confirm} onChange={set("confirm")} testid="ops-dialog-confirm" />}
        <Field label={t52("imp_d_target", { t: ov.target.canonical_host })} value={v.target_confirm} onChange={set("target_confirm")} testid="ops-dialog-target" />
        <Field label={t52("imp_d_totp")} value={v.totp_code} onChange={set("totp_code")} testid="ops-dialog-totp" numeric />
        <div className="acts">
          <button type="button" className="f52-btn f52-btn-secondary" onClick={onCancel} data-testid="ops-dialog-cancel">{t52("imp_d_cancel")}</button>
          <button type="submit" className="f52-btn f52-btn-danger-solid" disabled={busy} data-testid="ops-dialog-submit">
            {busy ? <Loader2 aria-hidden="true" className="f52-spin" /> : <ShieldCheck aria-hidden="true" />}{t52(sup ? "ops_sp_start" : start ? "ops_start" : "ops_resume")}
          </button>
        </div>
      </form>
    </div>
  );
}
